/*
 * The floating offers bar (Lena's boards SF 01, SF 03, SF 04; GUARDRAILS G9):
 * the salon's live offers as a row of cards over the page's own bottom bar,
 * theirs first. Claim ("Sign in" when signed out) signs a visitor in first, then claims; the salon's
 * server decides (CMS G15) and its reason is shown when it says no. ✕ hides
 * the bar for the rest of the visit; the Promotions page still lists all.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PromotionBadge from './PromotionBadge';
import { SIGNED_IN_EVENT } from './PromotionPopup';
import { CheckIcon, ChevronRightIcon, CloseIcon } from '../BookingNext/ServicesStep/icons';
import { getCMSJWT } from '../../services/auth.service';
import { promotionService } from '../../services/promotion.service';
import { storeService } from '../../services/store.service';
import { storeDay } from '../../utils/storeTime';
import { barCards, type BarCard } from '../../utils/promotionBar';
import styles from './PromotionBar.module.css';
import promoStyles from './Promotions.module.css';

const HIDDEN_KEY = 'promo_bar_hidden_v1';

interface Props {
  /** How far above the bottom of the screen it floats: over the page's own bar. */
  bottom: number;
  /** The location being booked, by name. */
  storeName?: string | null;
  /** The services step: theirs with no date rule reads "Applied" (SF 04). */
  onServices?: boolean;
  /** Open this page's sign-in; without it the app's own is opened. */
  onSignIn?: () => void;
  /** The home page: on a wide screen the bar is part of the page, every card whole, above the heading. */
  home?: boolean;
  /** Told whether the bar is on screen, so the page can make room for it. */
  onShown?: (shown: boolean) => void;
  /** Kept out of the way while the page has a sheet of its own open (the services step's cart). */
  away?: boolean;
}

const hiddenThisVisit = () => {
  try {
    return sessionStorage.getItem(HIDDEN_KEY) === '1';
  } catch {
    return false;
  }
};

const PromotionBar: React.FC<Props> = ({ bottom, storeName, onServices, onSignIn, home, onShown, away }) => {
  const navigate = useNavigate();
  const [cards, setCards] = useState<BarCard[]>([]);
  const [hidden, setHidden] = useState(hiddenThisVisit);
  const [active, setActive] = useState(0);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rowRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    let companyId: number;
    try {
      companyId = Number(await storeService.getCompanyId());
    } catch {
      return;
    }
    if (!companyId) return;
    const [catalog, mine] = await Promise.all([promotionService.getCatalog(companyId), promotionService.getMine()]);
    setCards(barCards(catalog, mine, { storeName, today: storeDay(Date.now()), onServices }));
  }, [storeName, onServices]);

  // A claim waiting on sign-in: the sign-in screen reloads the page, so it is picked up here.
  const claimPending = useCallback(async () => {
    const pending = promotionService.pendingClaim();
    if (!pending || !getCMSJWT()) return;
    promotionService.claimAfterSignIn(null);
    try {
      await promotionService.claim(pending);
    } catch (e) {
      setError(e instanceof Error ? e.message : "That offer couldn't be claimed.");
    }
    load();
  }, [load]);

  useEffect(() => {
    if (hidden) return;
    claimPending().then(load);
    window.addEventListener(SIGNED_IN_EVENT, claimPending);
    return () => window.removeEventListener(SIGNED_IN_EVENT, claimPending);
  }, [hidden, load, claimPending]);

  const shown = !hidden && !away && cards.length > 0;
  useEffect(() => {
    onShown?.(shown);
  }, [shown, onShown]);

  if (!shown) return null;

  const onScroll = () => {
    const row = rowRef.current;
    if (!row || !row.firstElementChild) return;
    const step = (row.firstElementChild as HTMLElement).offsetWidth + 10;
    setActive(Math.max(0, Math.min(cards.length - 1, Math.round(row.scrollLeft / step))));
  };

  const hide = () => {
    try {
      sessionStorage.setItem(HIDDEN_KEY, '1');
    } catch {
      /* hidden for this page only */
    }
    setHidden(true);
  };

  const claim = async (card: BarCard) => {
    setError(null);
    if (!getCMSJWT()) {
      promotionService.claimAfterSignIn(card.id);
      if (onSignIn) onSignIn();
      else window.dispatchEvent(new Event('mpg:open-login'));
      return;
    }
    setBusyId(card.id);
    try {
      await promotionService.claim(card.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "That offer couldn't be claimed.");
    } finally {
      setBusyId(null);
    }
  };

  const many = cards.length > 1;
  // Signed out, the card's button says what it does first (SF 61): sign in, then the claim goes through.
  const signedIn = !!getCMSJWT();

  return (
    <div
      className={`${styles.wrap} ${home ? styles.home : ''}`}
      style={{ ['--bar-bottom' as string]: `${bottom}px` }}
      role="region"
      aria-label="Offers"
    >
      <div className={styles.row} ref={rowRef} onScroll={onScroll}>
        {cards.map(card => (
          <div key={card.id} className={`${styles.card} ${many ? '' : styles.only}`}>
            <button type="button" className={styles.open} onClick={() => navigate('/promotions')}>
              <PromotionBadge icon={card.icon} size={36} />
              <span className={styles.text}>
                <span className={styles.title}>
                  {card.title}
                  {card.action === 'applied' && (
                    <span className={styles.tick} aria-label="Applied">
                      <CheckIcon size={14} strokeWidth={2.6} />
                    </span>
                  )}
                </span>
                <span className={styles.line}>{card.line}</span>
              </span>
            </button>
            {card.action === 'claimed' ? (
              // SF 01b, SF 04: the Promotions page's own chip, in the place of Claim, with a line tick.
              <span className={promoStyles.claimed}>
                <CheckIcon size={13} strokeWidth={2.6} />
                Claimed
              </span>
            ) : card.action === 'claim' ? (
              <button
                type="button"
                className={styles.claim}
                disabled={busyId === card.id}
                onClick={() => claim(card)}
                data-claim
              >
                {busyId === card.id ? 'Claiming…' : signedIn ? 'Claim' : 'Sign in'}
              </button>
            ) : (
              <span className={styles.chevron} aria-hidden="true">
                <ChevronRightIcon size={18} />
              </span>
            )}
          </div>
        ))}
      </div>
      {many && (
        <div className={styles.dots} aria-hidden="true">
          {cards.map((c, i) => (
            <span key={c.id} className={`${styles.dot} ${i === active ? styles.dotOn : ''}`} />
          ))}
        </div>
      )}
      <span className={styles.chip}>
        {many && <span className={styles.count}>{`${active + 1} / ${cards.length} ·`}</span>}
        <button type="button" className={styles.hide} aria-label="Hide offers" onClick={hide}>
          <CloseIcon size={12} strokeWidth={2.6} />
        </button>
      </span>
      {error && (
        <div className={styles.error} role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

export default PromotionBar;
